import {
  accountIdFormat,
  inventoryItemIdFormat,
  inventoryLevelIdFormat,
  inventoryLevelLedgerIdFormat,
  locationIdFormat,
  productIdFormat,
  storageLocationIdFormat,
} from "@redotech/redo-ids/ids";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum InventoryLevelAdjustmentKind {
  MANUAL = "manual",
  PICK = "pick",
  PUTAWAY = "putaway",
  RECEIVING = "receiving",
  TRANSFER = "transfer",
  REPLENISH = "replenish",
  BIN_COUNT = "bin_count",
  CYCLE_COUNT = "cycle_count",
  RETURN_RESTOCK = "return_restock",
  FULFILLMENT_CREATED = "fulfillment_created",
  FULFILLMENT_CANCELLED = "fulfillment_cancelled",
  PURCHASE_ORDER_RECEIVED = "purchase_order_received",
  FULFILLMENT_ORDER_CREATED = "fulfillment_order_created",
  FULFILLMENT_ORDER_CANCELLED = "fulfillment_order_cancelled",
  FULFILLMENT_ORDER_EDITED = "fulfillment_order_edited",
  SOT_INITIALIZATION = "sot_initialization",
  UNKNOWN = "unknown",
  VOID_LABEL = "void_label",
  WORK_ORDER = "work_order",
  FLUSH = "flush",
  CONVERSION = "conversion",
  SUPPLIES = "supplies",
}

export const InventoryLevelAdjustmentKindSchema = createEnum(
  InventoryLevelAdjustmentKind,
);

const InventoryLevelSchema_ = z.object({
  id: inventoryLevelIdFormat.schema,
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  storageLocationId: storageLocationIdFormat.schema,
  productId: productIdFormat.schema.nullable(),
  locationId: locationIdFormat.schema,
  locationAccountId: accountIdFormat.schema,
  productAccountId: accountIdFormat.schema.nullable(),
  quantity: z.number(),
  maxQuantity: z.number().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const InventoryLevelSchema: z.ZodType<InventoryLevel> =
  InventoryLevelSchema_;
export interface InventoryLevel extends z.infer<typeof InventoryLevelSchema_> {}

const InventoryLevelLedgerSchema_ = z.object({
  id: inventoryLevelLedgerIdFormat.schema,
  inventoryLevelId: inventoryLevelIdFormat.schema,
  delta: z.number(),
  kind: InventoryLevelAdjustmentKindSchema,
  referenceId: z.string().nullable(),
  notes: z.string().nullable(),
  legacyUserId: z.string().nullable().optional(),
  userName: z.string().nullable().optional(),
  createdAt: z.date(),
});

export const InventoryLevelLedgerSchema: z.ZodType<InventoryLevelLedger> =
  InventoryLevelLedgerSchema_;
export interface InventoryLevelLedger extends z.infer<
  typeof InventoryLevelLedgerSchema_
> {}

const InventoryLevelLedgerTableRowSchema_ = z.object({
  id: inventoryLevelLedgerIdFormat.schema,
  inventoryLevelId: inventoryLevelIdFormat.schema,
  accountId: accountIdFormat.schema,
  locationId: locationIdFormat.schema,
  storageLocationId: storageLocationIdFormat.schema,
  storageLocationName: z.string(),
  productId: productIdFormat.schema.nullable(),
  productImageUrl: z.string().nullish(),
  productSku: z.string().nullable(),
  productName: z.string().nullable(),
  delta: z.coerce.number(),
  kind: InventoryLevelAdjustmentKindSchema,
  referenceId: z.string().nullable(),
  notes: z.string().nullable(),
  userName: z.string().nullable().optional(),
  lotNumber: z.string().nullable().optional(),
  expirationDate: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
});

export const InventoryLevelLedgerTableRowSchema: z.ZodType<InventoryLevelLedgerTableRow> =
  InventoryLevelLedgerTableRowSchema_;
export interface InventoryLevelLedgerTableRow extends z.infer<
  typeof InventoryLevelLedgerTableRowSchema_
> {}
